/**
 * useAppInitialization.ts
 * Hook React pour initialiser l'application en mode React-first
 * Architecture React-Konva uniquement - plus de CanvasManager legacy
 */
import { useEffect, useRef, useState } from 'react';
import { logger } from '../utils/ProfessionalLogger';

// P3 : ImportMeta a déjà `env` défini par Vite ; on utilise un alias type plutôt
// qu'une extension (les types ne sont pas exactement compatibles).
type ImportMetaWithEnv = ImportMeta & { env?: { DEV?: boolean; [key: string]: unknown } };
import { DEFAULT_CONFIG } from '../types';
import { FontManager } from '../FontManager';
import { initializeLucideIconsSafely } from '../utils/lucideIcons';
import { performanceMonitor } from '../utils/PerformanceMonitor';
import { errorHandler } from '../utils/ErrorHandler';
import { testRunner } from '../utils/TestRunner';
import { documentationGenerator } from '../utils/DocumentationGenerator';
import Sortable from 'sortablejs';
import { appService } from '../services/AppService';
import { HistoryManager } from '../HistoryManager';
import { LayerManager } from '../LayerManager';
import { CloudManager } from '../CloudManager';
import { ReactFirstAppCoordinator } from '../services/ReactFirstAppCoordinator';

export interface UseAppInitializationResult {
  isInitialized: boolean;
  error: Error | null;
}

/**
 * Hook pour initialiser l'application complète en mode React-first
 * Initialise uniquement les services nécessaires pour React-Konva
 * Plus de CanvasManager legacy ni de container DOM caché
 */
export function useAppInitialization(): UseAppInitializationResult {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initializationAttemptedRef = useRef(false);

  useEffect(() => {
    // Éviter les initialisations multiples
    if (initializationAttemptedRef.current) {
      return;
    }
    initializationAttemptedRef.current = true;

    const initializeApp = async () => {
      try {
        logger.info('🚀 Initialisation de Snapbooth Template Editor (React-first)...');

        // 1. Initialiser le monitoring (dev mode seulement)
        if ((import.meta as ImportMetaWithEnv).env?.DEV) {
          performanceMonitor.startMonitoring();
        }
        errorHandler.setReportingEnabled(true);

        // 2. Initialiser FontManager
        const fontManager = FontManager.getInstance();
        fontManager.initialize();

        // 3. Initialiser les icônes Lucide
        try {
          initializeLucideIconsSafely();
          logger.info('✅ Icônes Lucide initialisées');
        } catch (err) {
          logger.error("Erreur lors de l'initialisation des icônes:", err);
        }

        // 4. Exposer SortableJS globalement pour compatibilité
        interface WindowWithSortable extends Window {
          Sortable?: typeof Sortable;
        }
        (window as WindowWithSortable).Sortable = Sortable;

        // 5. ARCHITECTURE REACT-FIRST: Plus de container DOM caché
        // Le canvas est géré entièrement par CanvasArea.tsx avec react-konva
        logger.info('ℹ️ Architecture React-first: canvas géré par CanvasArea.tsx (react-konva)');

        // 6. Initialiser les managers nécessaires (sans CanvasManager legacy)
        logger.info('🔧 Création des managers React-first...');

        // HistoryManager - nécessaire pour undo/redo
        // P3 : HistoryManager attend un number direct, pas un objet config.
        const historyManager = new HistoryManager(DEFAULT_CONFIG.maxHistoryStates);

        // LayerManager - nécessaire pour la gestion des calques
        const layerManager = new LayerManager();

        // CloudManager - nécessaire pour sauvegarde/chargement
        const cloudManager = new CloudManager();

        // 7. Créer ReactFirstAppCoordinator (remplace AppOrchestrator + AppCoordinator legacy)
        logger.info('🔧 Création de ReactFirstAppCoordinator...');
        const appCoordinator = new ReactFirstAppCoordinator({
          historyManager,
          fontManager,
          layerManager,
          cloudManager,
          defaultWidth: DEFAULT_CONFIG.defaultWidth,
          defaultHeight: DEFAULT_CONFIG.defaultHeight,
        });

        // 8. Enregistrer AppCoordinator dans appService
        appService.setAppCoordinator(appCoordinator, true);
        logger.info('✅ ReactFirstAppCoordinator enregistré dans appService');

        // 9. Initialiser HistoryMiddleware avec HistoryManager
        try {
          const { initializeHistoryMiddleware } = await import('../services/HistoryMiddleware');
          initializeHistoryMiddleware(historyManager);
          logger.info('✅ HistoryMiddleware initialisé');
        } catch (error) {
          logger.warn("⚠️ Erreur lors de l'initialisation de HistoryMiddleware:", error);
        }

        // 10. Vérification finale
        const finalCheck = appService.getAppCoordinator();
        if (!finalCheck) {
          logger.error('❌ ERREUR CRITIQUE: AppCoordinator toujours null après enregistrement');
          const error = new Error('AppCoordinator non disponible après initialisation');
          setError(error);
          return; // Ne pas mettre isInitialized à true
        }

        // 11. Exposer les outils de développement (dev mode)
        if ((import.meta as ImportMetaWithEnv).env?.DEV) {
          exposeDevTools();
        }

        logger.info('✅ Application initialisée avec succès (React-first)');
        setIsInitialized(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error("Erreur lors de l'initialisation de l'application:", error);
        setError(error);
      }
    };

    initializeApp();

    // Cleanup function - plus de cleanup nécessaire car on n'a plus de CanvasManager legacy
    return () => {
      logger.info('🧹 Cleanup de useAppInitialization (React-first)');
    };
  }, []); // Exécuter une seule fois au montage

  return { isInitialized, error };
}

/**
 * Expose les outils de développement dans window.devTools
 */
function exposeDevTools(): void {
  interface WindowWithDevTools extends Window {
    devTools?: {
      performance: typeof performanceMonitor;
      errors: typeof errorHandler;
      tests: typeof testRunner;
      [key: string]: unknown;
    };
  }
  (window as WindowWithDevTools).devTools = {
    performance: performanceMonitor,
    errors: errorHandler,
    tests: testRunner,
    documentation: documentationGenerator,
    runTests: async () => {
      logger.info('🧪 Running tests...');
      const results = await testRunner.runAllTests();
      logger.info('📊 Test results:', results);
      return results;
    },
    generateDocs: () => {
      logger.info('📚 Generating documentation...');
      const docs = documentationGenerator.generateDocumentation();
      logger.info('📄 Documentation generated:', docs);
      return docs;
    },
    exportDocs: () => {
      logger.info('📤 Exporting documentation...');
      documentationGenerator.exportToFile();
    },
    getPerformanceStats: () => {
      const stats = performanceMonitor.getPerformanceStats();
      logger.info('📊 Performance stats:', stats);
      return stats;
    },
    getErrorStats: () => {
      const stats = errorHandler.getErrorStats();
      logger.info('🚨 Error stats:', stats);
      return stats;
    },
    clearErrors: () => {
      errorHandler.clearErrors();
      logger.info('🧹 Errors cleared');
    },
    exportErrors: () => {
      const errors = errorHandler.exportErrors();
      logger.info('📤 Errors exported:', errors);
      return errors;
    },
  };
  logger.info('🛠️ Dev tools exposed to window.devTools');
}
